import { authNavigationTarget } from '../utils/auth-redirect';
import { BLUETICKS_TOKEN_KEY } from "@common/reusable/common";
import { MessageServiceActions } from "@common/services/messageServiceActions";
import { GetTokenResponse } from "@bt-common/interfaces/auth-bridge-contracts.interface";
import { notifyBackgroundOfLogout } from "./logout-bridge";
import { isExtensionContext } from "@common-services/detect-context";
import { authenticate, backendApiEndpoints, AuthResponse } from "@common-reducers/backend-api/backend-api";
import { AppThunkDispatch } from "@common/types/common-root-state-type";
import { clearAuthError, setAuthenticationStatus, setAuthError, setAuthFailure } from "@common-reducers/UserReducer";
import { classifyAuthFailure } from "./auth-failure";
import { readAttributionCookie } from "@common/utils/attributionReader";
import { readReferralCode } from "@common/utils/referral-code";
import { flowPath, isAuthPath, isClearFlow } from "@common/utils/clear-flow";

export interface AuthCredentials {
  email: string;
  password: string;
}

export interface SignupData {
  email: string;
  password: string;
}

export interface AuthResult {
  success: boolean;
  error?: string;
  /** Machine-readable failure kind, so the UI can show a localized message instead of the raw `error`. */
  code?: 'invalid_password' | 'account_exists';
  redirectTo?: string;
}

/**
 * Shape returned by the `authenticate` endpoint (local + jwt strategies). Extends the
 * RTK `AuthResponse` ({ accessToken, user }) with the Feathers `authentication` envelope
 * the login flow logs.
 */
export interface AuthenticateResult extends AuthResponse {
  authentication?: { strategy?: string; payload?: Record<string, unknown> };
}

/**
 * FE#80 (security) — strip the JWT `accessToken` param from the current URL once it has been
 * consumed for auth. Cross-property deep links (e.g. `blueticks.co/calendar?accessToken=<JWT>`)
 * otherwise leave a live credential in the address bar, where it is recorded by the page's GA tag,
 * saved in history/bookmarks, and forwarded in referrers (one such live token — `exp` ~year 2126 —
 * was observed leaving our perimeter as a GA4 referral).
 *
 * Uses `history.replaceState` (NOT pushState): the token-bearing URL is REPLACED in place, so it
 * never becomes a back-button history entry, and the clean URL is what any later navigation sends as
 * the referrer. Mirrors the cleanup logout already runs (UserThunks.logoutWrapperThunk). Never throws
 * — a URL-cleanup failure must not break auth. Returns whether a token was actually removed.
 */
export function stripAccessTokenFromUrl(): boolean {
  try {
    if (typeof window === 'undefined' || !window.location) return false;
    const url = new URL(window.location.href);
    if (!url.searchParams.has('accessToken')) return false;
    url.searchParams.delete('accessToken');
    // replaceState (not pushState) → no new history entry; the leaked URL is not back-navigable.
    window.history?.replaceState(null, '', url.toString());
    return true;
  } catch {
    return false;
  }
}

interface AuthErrorShape { status?: unknown; message?: unknown; data?: { message?: unknown; name?: unknown } }

/** Narrow an unknown thrown value to the fields we read; never trusts the shape. */
function asAuthError(error: unknown): AuthErrorShape {
  return typeof error === 'object' && error !== null ? error : {};
}

/** Status + message only: the whole error carries `data` (request/response bodies, emails). */
function describeAuthError(error: unknown): { status: unknown; message: unknown } {
  const e = asAuthError(error);
  return { status: e.status, message: e.data?.message || e.message };
}

export class AuthService {
  private static instance: AuthService | null = null;
  private lastAttemptedJwtToken: string | null = null;

  private constructor() { }

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }
  /**
   * Login with email and password
   */
  async login(credentials: AuthCredentials, dispatch: AppThunkDispatch): Promise<AuthResult> {
    dispatch(setAuthenticationStatus('userLoginStarted'));
    dispatch(clearAuthError());
    try {
      const impersonate = new URLSearchParams(window.location.search).get('impersonate') ?? undefined;
      console.log('[login-flow] AuthService.login: about to authenticate via local', { hasEmail: !!credentials?.email, hasPassword: !!credentials?.password, hasImpersonate: !!impersonate });

      let result: AuthenticateResult;
      try {
        // Trigger RTK Query mutation and unwrap the fulfilled payload
        const actionResult = dispatch(
          authenticate.initiate({ strategy: "local", ...credentials, impersonate })
        );
        result = await actionResult.unwrap();
      } catch (authError) {
        const authErr = authError as { status?: unknown; data?: { message?: unknown }; message?: unknown };
        console.log('[login-flow] AuthService.login: authenticate.local failed', { status: authErr?.status, message: authErr?.data?.message || authErr?.message });
        throw authError;
      }
      console.log('[login-flow] AuthService.login: authenticate.local returned', {
        hasResult: !!result,
        hasUser: !!result?.user,
        userId: result?.user?._id,
        hasAccessToken: !!result?.accessToken,
        authStrategy: result?.authentication?.strategy,
        payloadKeys: Object.keys(result?.authentication?.payload || {})
      });
      // onQueryStarted (the authenticate endpoint's single owner of auth state) has already set
      // credentials, persisted the token, and settled 'userLoginSuccess'. Nothing to do here.
      console.log('[login-flow] AuthService.login: local auth succeeded', { userId: result?.user?._id });
      // GuardedRoute handles the post-login redirect based on URL/session state.
      return { success: true };
    } catch (error: unknown) {
      const authErr = asAuthError(error);
      console.log('[login-flow] AuthService.login: authenticate.local failed', describeAuthError(error));
      const message = typeof authErr.data?.message === 'string' ? authErr.data.message : undefined;
      const errorMsg = message === "Invalid login"
        ? "Invalid password. Please retry or reset by 'Forgot your password?'"
        : message || "An error occurred during login";
      console.log('[login-flow] AuthService.login: local auth failed', { message: errorMsg });
      dispatch(setAuthenticationStatus('idle'));
      dispatch(setAuthError(errorMsg));
      return { success: false, error: errorMsg, ...(message === 'Invalid login' ? { code: 'invalid_password' as const } : {}) };
    }
  }

  /**
   * Signup and auto-login
   */
  async signup(signupData: SignupData, dispatch: AppThunkDispatch): Promise<AuthResult> {
    dispatch(setAuthenticationStatus('userRegisterStarted'));
    dispatch(clearAuthError());
    try {
      // Read attribution data from cookie
      const attribution = readAttributionCookie();
      console.log('[login-flow] AuthService.signup: about to register', {
        email: signupData?.email,
        hasAttribution: !!attribution,
        attributionKeys: attribution ? Object.keys(attribution) : []
      });

      await dispatch(
        backendApiEndpoints.register.initiate({
          email: signupData.email.toLowerCase(),
          password: signupData.password,
          attribution: attribution || undefined,
          signup_context: isExtensionContext() ? 'extension' : 'web_app',
          referralCode: readReferralCode(),
        })
      ).unwrap();
      console.log('[login-flow] AuthService.signup: register returned');

      dispatch(setAuthenticationStatus('userRegisterSuccess'));
      // Return redirect path for new signups
      return { success: true, redirectTo: isClearFlow() ? flowPath('/calendar?welcome=true') : '/onboarding?from=signup' };
    } catch (error: unknown) {
      console.log('[login-flow] AuthService.signup: register failed', describeAuthError(error));
      const signupMessage = asAuthError(error).data?.message;
      if (typeof signupMessage === 'string' && signupMessage.includes('email: value already exists.')) {
        console.log('[login-flow] AuthService.signup: email exists, attempting login');
        const loginResult = await this.login({ email: signupData.email, password: signupData.password }, dispatch);
        if (loginResult.code === 'invalid_password') {
          // The email is registered and the typed password is not theirs: this is not a "wrong
          // password" login attempt, it is a sign-up on an existing account. The form shows a
          // localized "account exists, log in" message from this code.
          dispatch(clearAuthError());
          return { success: false, code: 'account_exists' };
        }
        return loginResult;
      }

      const errorMessage = this.extractSignupErrorMessage(error);
      dispatch(setAuthError(errorMessage));
      dispatch(setAuthenticationStatus('idle'));
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Attempt JWT authentication on app start
   */
  async attemptJwtAuth(dispatch: AppThunkDispatch, options?: { force?: boolean }): Promise<boolean> {
    const token = await this.getStoredTokenAsync();
    if (!token) {
      console.log('[login-flow] AuthService.attemptJwtAuth: no token found');
      // Settle off the initial 'idle' (which route guards/popup treat as busy) to a terminal
      // failure so the UI falls through to the login form instead of spinning forever.
      dispatch(setAuthFailure('no-credential'));
      return false;
    }

    // Skip if we've already attempted auth with this exact token in this session
    if (!options?.force && this.lastAttemptedJwtToken === token) {
      console.log('[login-flow] AuthService.attemptJwtAuth: token already attempted in this session, skipping');
      return false;
    }

    // Mark as attempted to prevent repeated retries with the same token
    this.lastAttemptedJwtToken = token;

    try {
      console.log('[login-flow] AuthService.attemptJwtAuth: about to authenticate via jwt', { tokenLength: token?.length ?? 0 });
      dispatch(setAuthenticationStatus('autoJwtStarted'));
      // Race the JWT auth against a timeout so a hung/unreachable backend can't strand the UI
      // on an infinite loader — on timeout we reject into the catch, settling to 'autoJwtFail'.
      const AUTO_JWT_TIMEOUT_MS = 15000;
      let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
      try {
        await Promise.race([
          dispatch(authenticate.initiate({ strategy: "jwt", accessToken: token })).unwrap(),
          new Promise<never>((_, reject) => {
            timeoutHandle = setTimeout(() => reject(new Error('autoJwt timeout')), AUTO_JWT_TIMEOUT_MS);
          }),
        ]);
      } finally {
        if (timeoutHandle) clearTimeout(timeoutHandle);
      }
      // onQueryStarted owns credentials, token persistence, and the terminal status; reaching
      // here means the query fulfilled, so auth succeeded.
      console.log('[login-flow] AuthService.attemptJwtAuth: jwt auth succeeded');
      // FE#80 (security): the token is now authenticated + persisted to storage, so remove it from
      // the URL before it can be bookmarked, shared, put in history, or sent as a referrer. Strip
      // ONLY on success — a transient failure keeps the URL token so a retry can still use it (the
      // catch below deliberately does NOT clear the token for the same reason).
      stripAccessTokenFromUrl();
      return true;
    } catch (error) {
      console.log('[login-flow] AuthService.attemptJwtAuth: authenticate.jwt failed', describeAuthError(error));
      // Settle to a terminal failure status (matches onQueryStarted's jwt-failure path, and
      // also covers the timeout case where onQueryStarted hasn't settled yet). The token is NOT
      // cleared — it may still be valid; only an explicit logout clears it.
      // The REASON travels with the status: a rejected credential (401/403) is a dead session,
      // while a network error / timeout / 5xx is a backend blip that must not log anyone out.
      // Our own 'autoJwt timeout' Error lands here too and classifies as 'unreachable'.
      const failureKind = classifyAuthFailure(error);
      console.log('[login-flow] AuthService.attemptJwtAuth: classified failure', { failureKind });
      dispatch(setAuthFailure(failureKind));
      return false;
    }
  }

  /**
   * Check if JWT authentication should be attempted
   */
  shouldAttemptJwtAuth(): boolean {
    // Skip on login/signup pages (regular and clear flow)
    return !isAuthPath();
  }

  /**
   * Public helper to check if a token is available from any source
   */
  hasStoredToken(): boolean {
    try {
      return !!this.getStoredToken();
    } catch {
      return false;
    }
  }

  /**
   * Async token presence check (fetches from background in extension context)
   */
  async hasStoredTokenAsync(): Promise<boolean> {
    const token = await this.getStoredTokenAsync();
    return !!token;
  }

  /**
   * Public: the persisted auth token from the authoritative source
   * (background chrome.storage hub in extension contexts, else localStorage/
   * cookie/URL). Used by the API baseQuery as an Authorization fallback when
   * redux auth state hasn't hydrated — e.g. a gateway engine whose boot-time
   * POST /authentication (re-auth) raced a backend rollout. Without it, every request (notably the
   * wa-message-listener-event POST) goes out with NO auth header and 401s
   * silently, dropping the inbound event fleet-wide (BE#129 / wa-scheduler#129).
   */
  async getPersistedToken(): Promise<string | null> {
    return this.getStoredTokenAsync();
  }

  /**
   * Get stored token from appropriate location
   */
  private getStoredToken(): string | null {
    // Check localStorage first
    let token = localStorage.getItem(BLUETICKS_TOKEN_KEY);
    const hasLocalToken = !!token;
    if (hasLocalToken) {
      console.log('[login-flow] AuthService.getStoredToken: token found in localStorage');
    } else {
      console.log('[login-flow] AuthService.getStoredToken: no token in localStorage');
    }

    // Fallback to cookie if storage is empty
    if (!token && typeof document !== 'undefined') {
      const match = document.cookie.split('; ').find((row) => row.startsWith(`${BLUETICKS_TOKEN_KEY}=`));
      if (match) {
        token = decodeURIComponent(match.split('=')[1]);
        if (token) {
          console.log('[login-flow] AuthService.getStoredToken: token found in cookie');
        }
      }
    }

    // Check URL params for OAuth flows
    if (!token) {
      const params = new URLSearchParams(window.location.search);
      token = params.get('accessToken');
      if (token) {
        console.log('[login-flow] AuthService.getStoredToken: token found in URL params');
      }
    }

    // Check background service for extension
    if (!token && isExtensionContext()) {
      // This will be handled asynchronously in attemptJwtAuth
      console.log('[login-flow] AuthService.getStoredToken: extension context without token');
      return null;
    }

    return token;
  }

  /**
   * Async version of token retrieval that asks background in extension context
   */
  private async getStoredTokenAsync(): Promise<string | null> {
    try {
      // Extension PAGES (popup/options) — isExtensionContext() is true here but
      // FALSE in MV3 content scripts and on the web app. For these pages the
      // background's chrome.storage is the SYNCED source of truth: prefer it over
      // this page's own extension-origin localStorage, which can hold a STALE token
      // after a logout in another context (the logout broadcast clears tabs + the
      // hub, but not extension-page localStorage). If the hub is empty, drop the
      // stale local copy so a logout anywhere immediately logs the popup out.
      if (isExtensionContext()) {
        try {
          console.log('[login-flow] AuthService.getStoredTokenAsync: requesting token from background (authoritative)');
          const response = await MessageServiceActions.postMessageToBackgroundWithResponse<GetTokenResponse>('get-token', {});
          console.log('[login-flow] AuthService.getStoredTokenAsync: background response received', { hasResponse: !!response, hasAccessToken: !!response?.accessToken });
          const backgroundToken = response?.accessToken ?? null;
          if (backgroundToken) {
            try { localStorage.setItem(BLUETICKS_TOKEN_KEY, backgroundToken); } catch (e) { /* ignore */ }
            return backgroundToken;
          }
          // Hub is empty => any local copy is stale (e.g. logged out elsewhere).
          try { localStorage.removeItem(BLUETICKS_TOKEN_KEY); } catch (e) { /* ignore */ }
          return null;
        } catch (e) {
          console.log('[login-flow] AuthService.getStoredTokenAsync: background get-token failed; falling back to local', e);
          // Messaging failure (transient): fall back to local cache below.
        }
      }
      const localToken = this.getStoredToken();
      if (localToken) return localToken;
      return null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Clear stored token
   */
  private clearStoredToken(): void {
    localStorage.removeItem(BLUETICKS_TOKEN_KEY);
    // Reset attempted token marker when clearing stored token
    this.lastAttemptedJwtToken = null;

    // Mirror persistToken: clear the background/chrome.storage token from any context
    // that can message the background (incl. MV3 content scripts where chrome.extension
    // is undefined). Otherwise a WhatsApp-Web logout would leave a stale token behind.
    // Shared with logoutWrapperThunk so the transport stays consistent across surfaces.
    notifyBackgroundOfLogout();
  }



  /**
   * Persist token to storage and notify background/content where applicable
   */
  persistToken(token?: string | null): void {
    try {
      if (token) {
        localStorage.setItem(BLUETICKS_TOKEN_KEY, token);
        // Bridge the token to the background (chrome.storage) so OTHER extension
        // contexts (popup, options) can read it. NOTE: chrome.extension is undefined
        // in MV3 content scripts, so isExtensionContext() is false on web.whatsapp.com
        // — gating on it meant a WhatsApp-Web login never populated chrome.storage and
        // the popup couldn't auto-login. Gate on background-messaging availability
        // (chrome.runtime.id), which IS present in content scripts.
        const canMessageBackground = typeof chrome !== 'undefined' && !!chrome.runtime?.id;
        console.log('[login-flow] AuthService.persistToken: entry', { hasToken: true, canMessageBackground });
        if (canMessageBackground) {
          console.log('[login-flow] AuthService.persistToken: branch=runtime-id direct -> background "set-token"');
          // The background listener emits `message.data` to handlers, and
          // MessageServiceActions spreads the 2nd arg into the message root
          // (`{ cmd, ...data }`). So the payload MUST be nested under `data`,
          // otherwise handleSetToken receives `event = undefined` and the token
          // is never saved to chrome.storage (the long-standing bridge bug).
          MessageServiceActions.postMessageToBackgroundWithResponse<string>("set-token", { data: { accessToken: token } });
        } else if (typeof window !== 'undefined') {
          // Web-app page world (app.blueticks.co / localhost:3000): chrome.runtime
          // exists via externally_connectable but chrome.runtime.id is undefined,
          // so we can't message the background directly. Hand the token to the
          // co-injected content script (app.js / attribution-bridge.js) via a
          // window message; it has chrome.runtime.id and relays a `set-token` to
          // the background -> handleSetToken populates the chrome.storage hub so
          // the popup can auto-login. Keep the same `data`-nested shape the
          // background handler expects. Counterpart to the logout sync.
          console.log('[login-flow] AuthService.persistToken: branch=window.postMessage set-token (page world)');
          // Target THIS origin only (never '*') — the message carries the JWT, and the
          // co-injected content script shares this page's origin. A wildcard would leak
          // the token to any third-party frame listening for `message` in this document.
          window.postMessage({ cmd: 'set-token', data: { accessToken: token } }, window.location.origin);
        }
      } else {
        console.log('[login-flow] AuthService.persistToken: no token -> clearStoredToken()');
        this.clearStoredToken();
      }
    } catch (e) {
      // Swallow errors to avoid breaking auth flow
      // eslint-disable-next-line no-console
      console.log('[login-flow] AuthService.persistToken: failed', e);
    }
  }

  /**
   * Get redirect path after successful authentication
   */
  private getRedirectPath(): string {
    // Extension: no redirect
    if (isExtensionContext()) {
      return '';
    }

    // Web: check URL param only; otherwise default
    return authNavigationTarget(window.location.search, flowPath('/calendar'));
  }

  /**
   * Check if should redirect after auth
   */
  shouldRedirectAfterAuth(): boolean {
    return !isExtensionContext();
  }

  /**
   * Logout user
   */
  async logout(dispatch: AppThunkDispatch): Promise<void> {
    this.clearStoredToken();
    dispatch({ type: 'user/logout' });
  }

  /**
   * Validate email format
   */
  validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  }

  /**
   * Validate login data
   */
  validateLoginData(data: AuthCredentials): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!data.email || !this.validateEmail(data.email)) {
      errors.push('Please enter a valid email address');
    }

    if (!data.password) {
      errors.push('Password is required');
    }

    return { isValid: errors.length === 0, errors };
  }

  /**
   * Validate signup data
   */
  validateSignupData(data: SignupData): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!data.email || !this.validateEmail(data.email)) {
      errors.push('Please enter a valid email address');
    }

    if (!data.password || data.password.length < 6) {
      errors.push('Password must be at least 6 characters long');
    }

    return { isValid: errors.length === 0, errors };
  }

  private extractSignupErrorMessage(error: any): string {
    const errorData = error?.data;
    if (errorData?.name === 'Conflict') {
      return 'User already exists';
    }
    return errorData?.message || 'An error occurred during signup';
  }
}

export const authService = AuthService.getInstance();
